import { useCallback, useEffect, useRef, RefObject } from "react";

interface UsePopoverCloseOptions {
  /** Refs whose contents should NOT trigger close (container, portal, etc.) */
  refs?: RefObject<HTMLElement | null>[];
  /** Called after Escape close to restore focus (e.g. `triggerRef.current?.focus()`) */
  onEscapeFocus?: () => void;
  /** Whether to enable click-outside-to-close (default: true) */
  clickOutside?: boolean;
  /** When true, all close handlers are suspended (e.g. while a child modal is open) */
  paused?: boolean;
}

interface UsePopoverCloseReturn {
  containerRef: RefObject<HTMLElement | null>;
  handleBlur: (e: React.FocusEvent) => void;
}

/**
 * Handles Escape-to-close, click-outside-to-close, and tab-out-to-close for popover menus.
 *
 * @param open - Whether the popover is currently open
 * @param onClose - Called to close the popover
 * @param options - Configuration options
 * @returns containerRef to attach to the popover wrapper, and handleBlur for onBlur
 */
export default function usePopoverClose(
  open: boolean,
  onClose: () => void,
  options: UsePopoverCloseOptions = {}
): UsePopoverCloseReturn {
  const { refs: extraRefs = [], onEscapeFocus, clickOutside = true, paused = false } = options;
  const containerRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onEscapeFocusRef = useRef(onEscapeFocus);
  onEscapeFocusRef.current = onEscapeFocus;
  const lastTabAtRef = useRef(0);

  const allRefs = [containerRef, ...extraRefs];

  const isInsideRefs = useCallback(
    (target: EventTarget | null, path?: EventTarget[]) => {
      // Prefer composedPath when available — capture-phase touch events on iOS
      // and events crossing portals/shadow DOM can report a target that's not
      // the descendant `contains()` would expect. The path is authoritative.
      if (path && path.length) {
        return allRefs.some((ref) => ref.current && path.includes(ref.current));
      }
      if (!target || !(target instanceof Node)) return false;
      return allRefs.some((ref) => ref.current?.contains(target));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRefs.length]
  );

  // Escape key
  useEffect(() => {
    if (!open || paused) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (document.querySelector("[data-confirmation-dialog]")) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
        onEscapeFocusRef.current?.();
      } else if (event.key === "Tab") {
        // Stamp recent-Tab so handleBlur can distinguish a real tab-out from
        // a focus drop caused by CSS transitions / DOM mutations.
        lastTabAtRef.current = Date.now();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, paused]);

  // Click outside
  useEffect(() => {
    if (!open || !clickOutside || paused) return;

    function handleDocumentClick(event: MouseEvent | TouchEvent) {
      if (document.querySelector("[data-confirmation-dialog]")) return;
      const path = typeof event.composedPath === "function" ? event.composedPath() : [];
      if (isInsideRefs(event.target, path)) return;
      event.preventDefault();
      event.stopPropagation();
      onCloseRef.current();
    }

    const eventOptions = { capture: true } as const;
    document.addEventListener("mousedown", handleDocumentClick, eventOptions);
    document.addEventListener("touchstart", handleDocumentClick, eventOptions);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick, eventOptions);
      document.removeEventListener("touchstart", handleDocumentClick, eventOptions);
    };
  }, [open, clickOutside, paused, isInsideRefs]);

  // Tab-out (blur handler to attach to container via onBlur={handleBlur}).
  //
  // We have to distinguish two indistinguishable-by-relatedTarget cases:
  //   1. User Tab'd out of the popover → close.
  //   2. Focused child was hidden/unmounted (e.g. CSS transition on a
  //      closing dropdown option) → keep open; the document mousedown handler
  //      catches real outside clicks.
  // Both can produce relatedTarget=null/body. We disambiguate by the
  // recent-Tab timestamp set in the keydown handler.
  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (!open || paused || document.querySelector("[data-confirmation-dialog]")) return;
      const nextFocus = e.relatedTarget;
      if (nextFocus && isInsideRefs(nextFocus)) return;

      const fellThrough = !nextFocus || nextFocus === document.body || nextFocus === document.documentElement;
      if (fellThrough) {
        // Only treat as tab-out if a Tab key was pressed very recently.
        const tabbed = Date.now() - lastTabAtRef.current < 100;
        if (!tabbed) return;
      }
      onCloseRef.current();
    },
    [open, paused, isInsideRefs]
  );

  return { containerRef, handleBlur };
}
